/**
 * useTabVisibility — single coordinator for "tab became active" signals.
 *
 * 2026-05-27 — added during idle-tab-reconnect hardening to consolidate the
 * N independent `refetchOnWindowFocus: true` queries into one staggered
 * resume pipeline (see `<FocusResumeCoordinator>`). Pre-fix every query that
 * opted into focus-refetch fired in the same JS tick on tab resume, producing
 * a 9-10 request burst that blew the global 500/15min limiter even after the
 * recent skip-list bumps.
 *
 * Subscribes to BOTH `document.visibilitychange` AND `window.focus`/`blur` —
 * macOS Safari + some Chrome flavors miss `visibilitychange` when restoring
 * from a background space. `isVisible` is the OR of `!document.hidden` and
 * the last focus/blur edge; `lastResumedAt` bumps each time we transition
 * `false → true` so consumers can `useEffect` on it.
 */
import { useEffect, useState } from 'react';

export interface TabVisibilityState {
  isVisible: boolean;
  /**
   * Epoch ms when the tab most recently transitioned `hidden → visible`.
   * `null` until the first resume. Stable across re-renders (only updates on
   * an actual edge), so consumers can use it as a `useEffect` dep without
   * runaway re-fires.
   */
  lastResumedAt: number | null;
}

function computeVisible(): boolean {
  if (typeof document === 'undefined') return true;
  // Gate on the EXPLICIT hidden signal only — `document.hidden === true` is
  // the load-bearing "user can't see this tab" event. `document.hasFocus()`
  // returns false in JSDOM by default and in any cross-origin iframe, so
  // requiring it would suppress chrome in legitimate active sessions. The
  // window-`blur`/`focus` listener still adjusts state when the OS-level
  // focus moves away from the browser entirely.
  return !document.hidden;
}

export function useTabVisibility(): TabVisibilityState {
  const [state, setState] = useState<TabVisibilityState>(() => ({
    isVisible: computeVisible(),
    lastResumedAt: null,
  }));

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const handleChange = (): void => {
      const nextVisible = computeVisible();
      setState((prev) => {
        if (prev.isVisible === nextVisible) return prev;
        return {
          isVisible: nextVisible,
          lastResumedAt: nextVisible ? Date.now() : prev.lastResumedAt,
        };
      });
    };

    document.addEventListener('visibilitychange', handleChange);
    window.addEventListener('focus', handleChange);
    window.addEventListener('blur', handleChange);

    return () => {
      document.removeEventListener('visibilitychange', handleChange);
      window.removeEventListener('focus', handleChange);
      window.removeEventListener('blur', handleChange);
    };
  }, []);

  return state;
}
